import { Input } from "components/Inputs";
import { Breadcrumbs, Button, Header, LoadingState, View, Workspace } from "components/Shared";
import { translate } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import React, { memo, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { cleanText, dash } from "./formHelpers";
import { useFeatureFlagGuard } from "v2/hooks";

/**
 * Correct one lease (GET / PATCH /v2/lease-agreements/:id), reached from the
 * Details tab's Edit button.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS AND IS NOT WRITABLE
 * ---------------------------------------------------------------------------
 * Yardi owns this record; the form exists so a human can fix a field that
 * arrived wrong, not to author a lease. The API permits exactly the fields
 * below and `V2::LeaseAgreementsController#safe_params` documents what is out:
 * `tcode` and `property_id` are the dedup key the next /lease_data payload
 * matches on (changing either would orphan this lease's amendment tree), and
 * `current_term_amendment_id` is the structural pointer that amendment TYPE
 * decides, not a person.
 *
 * The term in force and the unit count are absent for a different reason: they
 * are not columns at all. They are folded from the amendment tree on read
 * (LeaseAgreement#starts_at / #ends_at / Amendment::UnitFold), so the way to
 * change them is to correct the amendment that carries the dates — the
 * Amendments tab's own Edit button.
 *
 * AND SO ARE THE COMMERCIAL FIELDS (Sim, 24 Aug): contracted area, term length,
 * unit status, unit of measure, the handover / opening / contraction / move-out
 * dates, fitout, sales category, RDD activity and LRTF notes are columns on the
 * AMENDMENT now, and their inputs went with them to AmendmentEdit. Each is a
 * statement about a term; one copy per lease meant the newest payload won.
 *
 * WHICH LEAVES TWO INPUTS. That is the whole form, and deliberately so — the
 * lease agreement is now TCode, Property, Category (Lease Type) and Lease Status,
 * and the first two are the dedup key.
 *
 * A later payload for the same TCode can still overwrite everything here.
 */

// Exactly what the API permits. Nothing else is seeded into form state, so
// nothing else can be sent by accident.
//
// TWO FIELDS SINCE 24 AUG (Sim). The commercial fields moved onto the amendment
// and so did their inputs — AmendmentEdit; nothing became read-only in that move,
// it is the same correction one screen along. `actual_opening_at` is not there
// either: it is gone from the record entirely, because nothing read it off the
// agreement (`Lease`, TCP's own, keeps its own column).
const WRITABLE = ["lease_type", "lease_status"];

const LeaseAgreementEdit = () => {
	const history = useHistory();
	const { id } = useParams<{ id: string }>();
	// null = still loading; false = the API refused, or the record is gone.
	const [agreement, setAgreement] = useState<tcpinpoint.LeaseAgreement | null | false>(null);
	const [data, setData] = useState<any>({});
	const [saving, setSaving] = useState(false);
	const [datasource] = useState(new Datasource({ mainModelName: "lease_agreement" }));

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			// The same subject carries the GET and the PATCH answer. Only the GET
			// seeds the form — re-seeding after a save would fight the redirect.
			if (!response.success) {
				setAgreement(previous => (previous === null ? false : previous));
				return;
			}
			const record = response.normalizedMainModelResponse;
			setAgreement(previous => (previous === null ? record || false : previous));
			setData((previous: any) => (_.isEmpty(previous) ? _.pick(record, WRITABLE) : previous));
		});
		return () => sub.unsubscribe();
	}, [datasource]);

	useEffect(() => {
		if (!_.isEmpty(id)) datasource.get("v2", `lease-agreements/${id}`, {});
	}, [datasource, id]);

	const onChange = (field: string) => (value: any) => setData(_.assign({}, data, { [field]: value }));

	const onSubmit = () => {
		setSaving(true);
		// Both are free text, so one pass. An empty box means "cleared", which is
		// null rather than "" — see formHelpers.
		const payload: any = {};
		_.forEach(WRITABLE, field => (payload[field] = cleanText(data[field])));

		datasource.put("v2", `lease-agreements/${id}`, { body: { lease_agreement: payload } }).then((response: any) => {
			setSaving(false);
			// `put` resolves on failure too (Datasource#call catches and pushes a
			// failed response), so only navigate when something came back.
			if (!!_.get(response, "normalizedMainModelResponse")) history.push(`/lease-agreements/${id}#details`);
		});
	};

	// BEHIND THE `lease-agreements` FLAG (Sim, 22 Sep 2026) — see the note on the
	// Show screens. Correcting Yardi data needs the section; a user without the
	// flag goes to the dashboard rather than seeing the 403's not-found state.
	const allowed = useFeatureFlagGuard("lease-agreements");
	if (!allowed) return null;

	if (agreement === null) return <LoadingState />;

	if (agreement === false)
		return (
			<React.Fragment>
				<Breadcrumbs items={[{ title: "lease_agreements", link: "/lease-agreements" }]} />
				<Header title="lease_agreement" stamp="lease_agreements" />
				<Workspace id="lease-agreement-edit">
					<View resource="lease_agreement">
						<div className="alert alert-light empty-state">{translate("lease_agreement_not_found")}</div>
					</View>
				</Workspace>
			</React.Fragment>
		);

	const title = agreement.tcode || (translate("lease_agreement") as string);

	return (
		<React.Fragment>
			<Breadcrumbs
				items={[
					{ title: "lease_agreements", link: "/lease-agreements" },
					{ title, link: `/lease-agreements/${id}` },
					{ title: "lease_agreement_edit" }
				]}
			/>
			<Header title={title} subtitle="lease_agreement_edit" stamp="lease_agreements" />
			<Workspace id="lease-agreement-edit">
				<View resource="lease_agreement" title="lease_agreement_edit">
					{/* Locked, and shown so the lock is legible — `is-locked` is what greys
					    them (see _form.scss). These two are the dedup key; see the header. */}
					<Input name="tcode" defaultState={agreement} disabled className="is-locked" formatter={dash} />
					<Input
						name="property_name"
						defaultState={agreement}
						disabled
						className="is-locked"
						formatter={dash}
					/>

					<hr style={{ margin: "1.5rem 0" }} />

					<Input name="lease_type" defaultState={data} onChange={onChange("lease_type")} />
					<Input name="lease_status" defaultState={data} onChange={onChange("lease_status")} />

					<Button onClick={onSubmit} loading={saving} disabled={saving}>
						{translate("save")}
					</Button>
				</View>
			</Workspace>
		</React.Fragment>
	);
};

export default memo(LeaseAgreementEdit);
